'use client';

import { FC, useEffect, useRef } from 'react';
import { Content, isFilled, asText } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import HighlightedText from '@/components/HighlightedText/HighlightedText';
import { useIsClient } from '@/hooks';

/**
 * Props for `FeatureListGrid`.
 */
export type FeatureListGridProps =
  SliceComponentProps<Content.FeatureListGridSlice>;

/**
 * Component for "FeatureListGrid" Slices.
 * A grid of feature cards with icons, titles, and descriptions.
 */
const FeatureListGrid: FC<FeatureListGridProps> = ({ slice }) => {
  const variation = slice.variation as string;
  const isSideTitle = variation === 'sideTitle';
  const isWhiteOppSide = variation === 'whiteOppSide';
  const isPathForward = variation === 'pathForward';

  const { section_title, section_summary, features } = slice.primary;

  // Extract fields that only exist on certain variations
  const section_intro =
    'section_intro' in slice.primary ? slice.primary.section_intro : null;
  const section_subtitle =
    'section_subtitle' in slice.primary ? slice.primary.section_subtitle : null;
  const kicker =
    'kicker' in slice.primary
      ? (slice.primary.kicker as string | null | undefined)
      : null;

  // Extract background from slice.primary
  const background =
    'background' in slice.primary ? slice.primary.background : null;
  const isFourCardLayout = features?.length === 4;

  const headerRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement>(null);
  const isClient = useIsClient();

  useEffect(() => {
    if (!isClient) {
      return;
    }

    const headerElement = headerRef.current;
    const cardsElement = cardsRef.current;

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -10% 0px',
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in');
          entry.target.classList.remove('opacity-0', 'translate-y-8');
        }
      });
    }, observerOptions);

    if (headerElement) {
      headerElement.style.transitionDelay = '0ms';
      observer.observe(headerElement);
    }

    if (cardsElement) {
      cardsElement.style.transitionDelay = '200ms';
      observer.observe(cardsElement);
    }

    return () => {
      if (headerElement) {
        observer.unobserve(headerElement);
      }
      if (cardsElement) {
        observer.unobserve(cardsElement);
      }
    };
  }, [isClient]);

  // Render pathForward variation — 3-col grid with optional highlighted "outcome" card
  if (isPathForward) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="bg-[#01060a] py-16 md:py-24"
      >
        <div className="mx-auto w-full max-w-[1140px] px-4 md:px-8">
          <div
            ref={headerRef}
            className="flex flex-col items-center gap-5 text-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {kicker && (
              <div className="rounded-full border border-[#00f557] px-5 py-2">
                <span className="text-[12px] font-normal uppercase tracking-[0.04em] text-white md:text-[14px]">
                  {kicker}
                </span>
              </div>
            )}

            {isFilled.richText(section_title) && (
              <h2 className="max-w-[1040px] text-[32px] font-semibold leading-[1.15] text-white md:text-[48px]">
                <HighlightedText
                  text={asText(section_title) || ''}
                  highlightClassName="text-[#00f557]"
                />
              </h2>
            )}

            {isFilled.richText(section_summary) && (
              <div className="max-w-[820px]">
                <PrismicRichText
                  field={section_summary}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-[15px] leading-[1.5] text-white/75 md:text-[16px]">
                        {children}
                      </p>
                    ),
                    strong: ({ children }) => (
                      <strong className="font-semibold">{children}</strong>
                    ),
                  }}
                />
              </div>
            )}
          </div>

          {features && features.length > 0 && (
            <div
              ref={cardsRef}
              className="mt-12 grid grid-cols-1 gap-px bg-[#1a1a2e] sm:grid-cols-2 md:mt-16 lg:grid-cols-3 rounded-[12px] overflow-hidden opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {features.map((feature, index) => {
                const highlighted = (feature as { is_highlighted?: boolean })
                  .is_highlighted;
                const cardCls = highlighted
                  ? 'flex flex-col gap-3 bg-[#01060a] p-7 md:p-8 ring-2 ring-inset ring-[#00f557]'
                  : 'flex flex-col gap-3 bg-[#0a0f1c] p-7 md:p-8';

                return (
                  <div key={index} className={cardCls}>
                    {isFilled.image(feature.icon) && (
                      <div className="h-9 w-9 shrink-0">
                        <PrismicNextImage
                          field={feature.icon}
                          className="h-full w-full object-contain"
                          fallbackAlt=""
                        />
                      </div>
                    )}

                    {feature.title && (
                      <h3
                        className={`text-[20px] font-semibold leading-[1.3] md:text-[22px] ${
                          highlighted ? 'text-[#00f557]' : 'text-white'
                        }`}
                      >
                        {feature.title}
                      </h3>
                    )}

                    {feature.description && (
                      <p
                        className={`text-[14px] leading-[1.5] md:text-[15px] ${
                          highlighted ? 'text-[#00f557]' : 'text-white/70'
                        }`}
                      >
                        {feature.description}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </section>
    );
  }

  // Render whiteOppSide variation
  if (isWhiteOppSide) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="bg-white py-[50px] pt-[94px] px-5 md:px-16"
      >
        <div className="max-w-[1040px] mx-auto">
          <div
            ref={cardsRef}
            className="flex flex-col lg:flex-row gap-6 items-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Title & Description - Left Side */}
            <div
              ref={headerRef}
              className="w-full lg:w-[371px] shrink-0 flex flex-col gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {/* Section Title */}
              {isFilled.richText(section_title) && (
                <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-[#041018]">
                  <HighlightedText
                    text={asText(section_title) || ''}
                    highlightClassName="text-[#069183]"
                  />
                </h2>
              )}

              {/* Section Summary */}
              {isFilled.richText(section_summary) && (
                <PrismicRichText
                  field={section_summary}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-lg font-normal leading-[1.4] text-[#041018]">
                        {children}
                      </p>
                    ),
                  }}
                />
              )}
            </div>

            {/* Feature Cards Grid - Right Side */}
            {features && features.length > 0 && (
              <div className="flex-1 flex flex-wrap gap-4 items-start justify-center lg:justify-start">
                {features.map((feature, index) => {
                  // Alternate between white and light green in checkerboard pattern
                  const row = Math.floor(index / 2);
                  const col = index % 2;
                  const isGreen =
                    (row % 2 === 0 && col === 1) ||
                    (row % 2 === 1 && col === 0);
                  const bgColor = isGreen ? 'bg-[#f4fcf7]' : 'bg-white';
                  const borderColor = isGreen
                    ? 'border-white'
                    : 'border-[#dedee9]';

                  return (
                    <div
                      key={index}
                      className={`w-full sm:w-[calc(50%-8px)] rounded-xl p-4 shadow-[0px_4px_12px_0px_rgba(13,10,44,0.06)] flex flex-col gap-2 items-start border ${bgColor} ${borderColor}`}
                    >
                      {/* Icon & Title */}
                      <div className="flex flex-col gap-2 items-start justify-center w-full">
                        {/* Icon */}
                        {isFilled.image(feature.icon) && (
                          <div className="w-12 h-12 shrink-0">
                            <PrismicNextImage
                              field={feature.icon}
                              className="w-full h-full object-contain"
                            />
                          </div>
                        )}

                        {/* Title */}
                        {feature.title && (
                          <h3 className="text-xl font-semibold leading-[1.4] text-[#041018]">
                            {feature.title}
                          </h3>
                        )}
                      </div>

                      {/* Description */}
                      {feature.description && (
                        <p className="text-lg font-normal leading-[1.4] text-[#041018]">
                          {feature.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </section>
    );
  }

  // Render sideTitle variation
  if (isSideTitle) {
    return (
      <section
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative py-[80px] px-4 md:px-8 lg:px-[200px]"
        style={{ backgroundColor: '#0e1010' }}
      >
        {/* Background Image Layer */}
        {isFilled.image(background) && (
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <PrismicNextImage
              field={background}
              className="absolute w-full h-[226%] left-0 top-[-12%] object-cover max-w-none opacity-50"
              fill={false}
              priority
            />
          </div>
        )}

        <div className="relative max-w-[1140px] mx-auto">
          <div
            ref={cardsRef}
            className="flex flex-col lg:flex-row gap-10 items-center opacity-0 translate-y-8 transition-all duration-700 ease-out"
          >
            {/* Feature Cards Grid - Left Side */}
            {features && features.length > 0 && (
              <div className="flex-1 flex flex-wrap gap-6 items-center justify-center">
                {features.map((feature, index) => {
                  // Alternate between #005e4e and #002726 in 2x2 checkerboard pattern
                  const row = Math.floor(index / 2);
                  const col = index % 2;
                  const bgColor =
                    (row % 2 === 0 && col === 0) || (row % 2 === 1 && col === 1)
                      ? '#005e4e'
                      : '#002726';

                  return (
                    <div
                      key={index}
                      className="w-full sm:w-[265px] shrink-0 backdrop-blur-[10px] rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] flex flex-col gap-3 items-start justify-center overflow-hidden"
                      style={{ backgroundColor: bgColor }}
                    >
                      {/* Icon */}
                      {isFilled.image(feature.icon) && (
                        <div className="w-12 h-12 shrink-0">
                          <PrismicNextImage
                            field={feature.icon}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      )}

                      {/* Title */}
                      {feature.title && (
                        <h3 className="text-xl font-semibold leading-[1.4] text-[#eee]">
                          {feature.title}
                        </h3>
                      )}

                      {/* Description */}
                      {feature.description && (
                        <p className="text-lg font-normal leading-[1.4] text-[#eee]">
                          {feature.description}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Title & Description - Right Side */}
            <div
              ref={headerRef}
              className="w-full lg:w-[446px] shrink-0 flex flex-col gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out"
            >
              {/* Section Title */}
              {isFilled.richText(section_title) && (
                <PrismicRichText
                  field={section_title}
                  components={{
                    heading2: ({ children }) => (
                      <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white">
                        {children}
                      </h2>
                    ),
                  }}
                />
              )}

              {/* Section Summary */}
              {isFilled.richText(section_summary) && (
                <PrismicRichText
                  field={section_summary}
                  components={{
                    paragraph: ({ children }) => (
                      <p className="text-lg font-normal leading-[1.5] text-white">
                        {children}
                      </p>
                    ),
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </section>
    );
  }

  // Default Grid variation
  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative py-[80px] px-4 md:px-8 lg:px-[150px]"
      style={{ backgroundColor: '#0e1010' }}
    >
      {/* Background Image Layer */}
      {isFilled.image(background) && (
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <PrismicNextImage
            field={background}
            className="absolute w-full h-[226%] left-0 top-[-12%] object-cover max-w-none opacity-50"
            fill={false}
            priority
          />
        </div>
      )}

      <div className="relative max-w-[1140px] mx-auto flex flex-col items-center gap-6">
        {/* Header Section */}
        <div
          ref={headerRef}
          className="flex flex-col items-center gap-6 w-full opacity-0 translate-y-8 transition-all duration-700 ease-out"
        >
          {/* Section Title */}
          {isFilled.richText(section_title) && (
            <PrismicRichText
              field={section_title}
              components={{
                heading2: ({ children }) => (
                  <h2 className="text-[32px] md:text-[42px] font-medium leading-[1.2] text-white text-center">
                    {children}
                  </h2>
                ),
              }}
            />
          )}

          {/* Section Summary */}
          {isFilled.richText(section_summary) && (
            <div className="max-w-[820px]">
              <PrismicRichText
                field={section_summary}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-lg md:text-xl font-normal leading-[1.5] text-white text-center">
                      {children}
                    </p>
                  ),
                }}
              />
            </div>
          )}

          {/* Section Introduction */}
          {isFilled.richText(section_intro) && (
            <div className="max-w-[820px]">
              <PrismicRichText
                field={section_intro}
                components={{
                  paragraph: ({ children }) => (
                    <p className="text-lg md:text-xl font-normal leading-[1.5] text-white text-center">
                      {children}
                    </p>
                  ),
                }}
              />
            </div>
          )}

          {/* Section Subtitle */}
          {isFilled.richText(section_subtitle) && (
            <PrismicRichText
              field={section_subtitle}
              components={{
                heading4: ({ children }) => (
                  <h4 className="text-xl md:text-2xl font-bold leading-[1.4] text-white text-center">
                    {children}
                  </h4>
                ),
              }}
            />
          )}
        </div>

        {/* Feature Cards Grid */}
        {features && features.length > 0 && (
          <div
            ref={cardsRef}
            className={`grid w-full gap-6 opacity-0 translate-y-8 transition-all duration-700 ease-out grid-cols-1 sm:grid-cols-2 ${
              isFourCardLayout ? 'lg:grid-cols-2' : 'lg:grid-cols-3'
            }`}
          >
            {features.map((feature, index) => {
              const columns = isFourCardLayout ? 2 : 3;
              // Alternate between #005e4e and #002726 in checkerboard pattern
              const isEvenRow = Math.floor(index / columns) % 2 === 0;
              const isEvenCol = (index % columns) % 2 === 0;
              const bgColor =
                (isEvenRow && isEvenCol) || (!isEvenRow && !isEvenCol)
                  ? '#005e4e'
                  : '#002726';

              return (
                <div
                  key={index}
                  className="w-full min-w-0 backdrop-blur-[10px] rounded-2xl p-6 shadow-[0px_4.325px_64.871px_0px_rgba(0,0,0,0.08)] flex flex-col gap-3 items-start justify-center overflow-hidden"
                  style={{ backgroundColor: bgColor }}
                >
                  {/* Icon */}
                  {isFilled.image(feature.icon) && (
                    <div className="w-12 h-12 shrink-0">
                      <PrismicNextImage
                        field={feature.icon}
                        className="w-full h-full object-contain"
                      />
                    </div>
                  )}

                  {/* Title */}
                  {feature.title && (
                    <h3 className="text-xl font-bold leading-[1.4] text-[#eee]">
                      {feature.title}
                    </h3>
                  )}

                  {/* Description */}
                  {feature.description && (
                    <p className="text-xl font-normal leading-[1.4] text-[#eee]">
                      {feature.description}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
};

export default FeatureListGrid;
